import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Clock, X } from 'lucide-react';
import useRolloverToastStore from '@/stores/useRolloverToastStore';
import useSlotSelectionStore from '@/stores/useSlotSelectionStore';
import useChangeSlotStore from '@/stores/useChangeSlotStore';
import useDeferConfirmStore from '@/stores/useDeferConfirmStore';
import { useRespondPickupSlot } from '@/hooksApi';
import { formatSlotWindow12 } from '@/utils/helpers';

interface RolloverToastProps {
  // Gate to the Pickups tab only — this component is mounted unconditionally
  // as a singleton at the pickups page root (see index.tsx).
  active?: boolean;
}

// Bottom-corner push notification that appears the moment a pickup's slot
// rolls over to a later window AND the response window is still open
// (slot_response_open). Companion to — not a replacement for — the in-row
// Accept/Change/Defer buttons (PickupSlotCell): both operate on the same
// pickup and either can resolve it first.
const RolloverToast = ({ active = true }: RolloverToastProps) => {
  const { t } = useTranslation();
  const { data, dismiss } = useRolloverToastStore();
  const isSlotSelectionModalOpen = useSlotSelectionStore((state) => state.isOpen);
  const openChangeSlot = useChangeSlotStore((state) => state.openChangeSlot);
  const openDeferConfirm = useDeferConfirmStore((state) => state.openDeferConfirm);
  const { mutate: respondSlot, isLoading: isResponding } = useRespondPickupSlot();

  // Never stack on top of the mandatory gate, and never show outside the
  // Pickups tab.
  if (!active || !data || isSlotSelectionModalOpen) return null;

  const handleAccept = () => {
    respondSlot(
      { uid: data.pickupUid, choice: 'accept' },
      {
        onSuccess: () => {
          message.success(String(t('operation')));
          dismiss(data.pickupUid);
        },
      },
    );
  };

  const handleChangeSameDay = () => {
    dismiss(data.pickupUid);
    openChangeSlot({ uid: data.pickupUid, currentCode: data.toSlotCode, mode: 'respond' });
  };

  const handleDefer = () => {
    respondSlot(
      { uid: data.pickupUid, choice: 'defer' },
      {
        onSuccess: (res) => {
          dismiss(data.pickupUid);
          // A defer rebooks the pickup onto its ORIGINAL slot on the next
          // operational day — show THAT landing slot (resulting_slot), falling
          // back to the local rolled-to values only when the API omits it.
          const slot = res?.resulting_slot;
          openDeferConfirm({
            label: slot?.label ?? data.toSlotLabel,
            window: slot ? formatSlotWindow12(slot.window_from, slot.window_to) : data.toSlotWindow,
          });
        },
      },
    );
  };

  return (
    <div
      // v2-task-8b (§2.3 shadow-floating token): a bottom-corner floating
      // card should sit between shadow-raised and shadow-overlay, not
      // exceed the modal's own elevation (shadow-2xl was heavier/darker
      // than the modal, the wrong z-order read).
      // v2-task-12fix (H1): `border` utilities below only carry border-WIDTH
      // with Preflight off — `border-solid` restores the visible outlines.
      className='slot-rollover-toast fixed bottom-6 end-6 z-[1010] w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-solid shadow-[0_12px_32px_rgba(0,0,0,0.12)]'
      style={{ background: '#fff', borderColor: 'rgb(232,230,227)' }}
    >
      <div className='flex items-center gap-2 bg-amber-50 px-4 py-3'>
        <AlertTriangle size={16} className='shrink-0 text-amber-500' />
        <span className='flex-1 text-sm font-bold text-amber-700'>
          {String(t('slot_rollover_toast_title'))}
        </span>
        <button
          type='button'
          aria-label={String(t('close'))}
          onClick={() => dismiss(data.pickupUid)}
          // v2-polish: rounded-md (6px) is a retired off-scale radius (§2.1);
          // rounded-lg (8px, radius-sm) brings it onto the 4-step scale. This
          // close control was also the one interactive element in the feature
          // still missing the mandatory §2.6 focus-visible ring + active press.
          className='-me-1 rounded-lg p-1 transition-colors hover:bg-amber-100 active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
        >
          <X size={14} className='text-amber-600' />
        </button>
      </div>
      <div className='p-4'>
        <p className='text-sm text-[rgb(115,123,140)]'>
          {String(
            t('slot_rollover_toast_body', { label: data.toSlotLabel, window: data.toSlotWindow }),
          )}
        </p>
        {/* §8 — accepting a rollover moves the pickup to a NEW slot, which
            resets any preferred timing back to Anytime. Surface that up front so
            it's never a surprise; the merchant can re-set it from the shipments
            page. */}
        <div
          className='mt-2.5 flex items-center gap-2 rounded-lg px-3 py-2'
          style={{ background: 'rgb(245,245,243)' }}
        >
          <Clock size={14} className='shrink-0 text-[rgb(115,123,140)]' />
          <span className='text-xs text-[rgb(115,123,140)]'>
            {String(t('preferred_timing_rollover_note'))}
          </span>
        </div>
        <div className='mt-3 flex flex-col gap-2'>
          <button
            type='button'
            disabled={isResponding}
            onClick={handleAccept}
            className='flex h-10 items-center justify-center gap-1.5 rounded-xl bg-[rgb(237,224,74)] text-sm font-semibold text-[rgb(57,57,60)] transition-colors hover:bg-[rgb(230,217,60)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
          >
            <CheckCircle2 size={15} />
            {/* QA 2026-07-22 (mobile parity): show the actual slot window in the
                CTA, not the slot code. Falls back to the code when the row
                carried no window. */}
            {String(t('slot_toast_accept_cta', { code: data.toSlotWindow || data.toSlotCode }))}
          </button>
          <div className='flex gap-2'>
            <button
              type='button'
              disabled={isResponding}
              onClick={handleChangeSameDay}
              className='h-10 flex-1 rounded-xl border border-solid text-xs font-semibold transition-colors hover:bg-[rgb(245,245,243)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
              style={{ borderColor: 'rgb(232,230,227)' }}
            >
              {String(t('slot_change_same_day'))}
            </button>
            <button
              type='button'
              disabled={isResponding}
              onClick={handleDefer}
              className='h-10 flex-1 rounded-xl border border-solid text-xs font-semibold transition-colors hover:bg-[rgb(245,245,243)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
              style={{ borderColor: 'rgb(232,230,227)' }}
            >
              {String(t('slot_defer_next_day'))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolloverToast;
